import { useEffect, useId, useRef, useState, type FocusEvent, type ReactNode } from 'react';
import styles from './HeaderMenu.module.css';

export interface HeaderMenuProps {
  /** Accessible name for the trigger button. */
  label: string;
  /** Trigger inner content (icon/text; decorative glyphs should be aria-hidden). */
  trigger: ReactNode;
  /** Panel edge alignment relative to the trigger. */
  align?: 'start' | 'end';
  /** Render-prop receiving a `close()` to call from item handlers. */
  children: (close: () => void) => ReactNode;
  id?: string;
}

/**
 * Accessible disclosure dropdown for the app header (settings + account menus).
 * A `<button>` toggles a panel of links/actions; closes on Esc (focus returns to
 * the trigger), outside click, focus-out, and any activation inside the panel.
 * Non-modal — focus is never trapped.
 */
export function HeaderMenu({ label, trigger, align = 'end', children, id }: HeaderMenuProps) {
  const autoId = useId();
  const panelId = `${id ?? autoId}-panel`;
  const [open, setOpen] = useState(false);
  const triggerRef = useRef<HTMLButtonElement>(null);
  const rootRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    if (!open) return;
    const onKey = (e: KeyboardEvent) => {
      if (e.key === 'Escape') {
        e.stopPropagation();
        setOpen(false);
        triggerRef.current?.focus();
      }
    };
    const onPointer = (e: MouseEvent) => {
      if (!rootRef.current?.contains(e.target as Node)) setOpen(false);
    };
    document.addEventListener('keydown', onKey, true);
    document.addEventListener('mousedown', onPointer);
    return () => {
      document.removeEventListener('keydown', onKey, true);
      document.removeEventListener('mousedown', onPointer);
    };
  }, [open]);

  const onBlur = (e: FocusEvent<HTMLDivElement>) => {
    if (!e.currentTarget.contains(e.relatedTarget as Node | null)) setOpen(false);
  };

  return (
    <div ref={rootRef} className={styles.root} onBlur={onBlur}>
      <button
        ref={triggerRef}
        type="button"
        className={styles.trigger}
        aria-haspopup="true"
        aria-expanded={open}
        aria-controls={panelId}
        aria-label={label}
        onClick={() => setOpen((v) => !v)}
      >
        {trigger}
      </button>
      {open ? (
        <div
          id={panelId}
          className={`${styles.panel} ${align === 'start' ? styles.start : styles.end}`}
        >
          {children(() => setOpen(false))}
        </div>
      ) : null}
    </div>
  );
}
